import React, { useState } from "react";
import { Button, Switch, Tooltip, Popconfirm, Tag, Space } from "antd";
import {
  UpOutlined,
  DownOutlined,
  CopyOutlined,
  DeleteOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
} from "@ant-design/icons";
import { SECTION_REGISTRY, renderAutoloadSectionForm, canSectionBeDeleted } from "./autoload";
import { CMS_THEME } from "./constant";

const BlockCard = ({
  form,
  section,
  index,
  totalSections,
  isExpanded,
  onSelect,
  onUpdateSection,
  onMoveUp,
  onMoveDown,
  onDuplicate,
  onDelete,
}) => {
  const [hovered, setHovered] = useState(false);
  const allowDelete = canSectionBeDeleted(section);

  const registryItem = SECTION_REGISTRY.find(
    (r) => r.type === section.type,
  ) || {
    name: section.title || section.type,
    icon: null,
  };

  const renderSectionForm = () => {
    return renderAutoloadSectionForm(
      section,
      (data) => onUpdateSection(index, { ...section, data }),
      form
    );
  };

  const isEnabled = section.enabled !== false;

  return (
    <div
      style={{
        borderRadius: 8,
        border: isExpanded
          ? `1px solid ${CMS_THEME.activeBorder}`
          : hovered
            ? `1px solid ${CMS_THEME.hoveredBorder}`
            : `1px solid ${CMS_THEME.defaultBorder}`,
        background: isExpanded
          ? CMS_THEME.activeBackground
          : hovered
            ? CMS_THEME.hoveredBackground
            : CMS_THEME.defaultBackground,
        marginBottom: 12,
        transition: "all 0.15s ease",
        boxShadow: isExpanded ? "0 2px 8px rgba(0,0,0,0.06)" : "none",
        opacity: isEnabled ? 1 : 0.7,
      }}
    >
      {/* Block Card Header Bar (Tykkit style) */}
      <div
        onClick={onSelect}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          padding: "12px 16px",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            flex: 1,
            minWidth: 0,
          }}
        >
          {/* Letter / Icon Avatar */}

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span
                style={{
                  fontWeight: 600,
                  fontSize: 14,
                  color: isExpanded
                    ? CMS_THEME.activeText
                    : CMS_THEME.defaultText,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {section.title || registryItem.name}
              </span>
              <Tag
                color={isEnabled ? "blue" : "default"}
                style={{ margin: 0, fontSize: 11 }}
              >
                {section.type}
              </Tag>
              {!isEnabled && <Tag color="error">Disabled</Tag>}
            </div>

            <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>
              Section #{index + 1} {isExpanded ? "• editing block content" : ""}
            </div>
          </div>
        </div>

        {/* Header Action Buttons */}
        <Space size={4} onClick={(e) => e.stopPropagation()}>
          <Tooltip title={isEnabled ? "Disable Section" : "Enable Section"}>
            <Switch
              size="small"
              checked={isEnabled}
              onChange={(checked) =>
                onUpdateSection(index, { ...section, enabled: checked })
              }
              checkedChildren={<EyeOutlined />}
              unCheckedChildren={<EyeInvisibleOutlined />}
            />
          </Tooltip>

          {index > 0 && (
            <Tooltip title="Move Up">
              <Button
                type="text"
                size="small"
                icon={<UpOutlined style={{ fontSize: 12 }} />}
                onClick={onMoveUp}
              />
            </Tooltip>
          )}

          {index < totalSections - 1 && (
            <Tooltip title="Move Down">
              <Button
                type="text"
                size="small"
                icon={<DownOutlined style={{ fontSize: 12 }} />}
                onClick={onMoveDown}
              />
            </Tooltip>
          )}

          <Tooltip title="Duplicate Block">
            <Button
              type="text"
              size="small"
              icon={<CopyOutlined style={{ fontSize: 12 }} />}
              onClick={onDuplicate}
            />
          </Tooltip>

          {allowDelete ? (
            <Popconfirm
              title="Delete this section block?"
              okText="Delete"
              okButtonProps={{ danger: true, size: "small" }}
              cancelText="Cancel"
              onConfirm={(e) => {
                if (e) e.stopPropagation();
                onDelete();
              }}
              getPopupContainer={(triggerNode) => triggerNode.parentNode}
            >
              <Tooltip title="Delete Block">
                <Button
                  type="text"
                  danger
                  size="small"
                  icon={<DeleteOutlined style={{ fontSize: 12 }} />}
                  onClick={(e) => e.stopPropagation()}
                />
              </Tooltip>
            </Popconfirm>
          ) : (
            <Tooltip title="Protected Block (Cannot be deleted)">
              <Button
                type="text"
                disabled
                size="small"
                icon={<DeleteOutlined style={{ fontSize: 12, color: "#d1d5db" }} />}
              />
            </Tooltip>
          )}
        </Space>
      </div>

      {/* Expandable Form Body */}
      {isExpanded && (
        <div
          style={{
            padding: "16px 20px",
            borderTop: "1px solid #f0f0f0",
            background: "#ffffff",
            borderRadius: "0 0 8px 8px",
          }}
        >
          {renderSectionForm()}
        </div>
      )}
    </div>
  );
};

export default BlockCard;
